<template>
  <transition name="esocial-modal-fade">
    <div class="esocial-modal-overlay" v-if="showModal" @click.self="close">
      <div class="esocial-modal esocial-modal--wide">
        <div class="esocial-modal__header">
          <span class="esocial-modal__title">Rotinas Automáticas do eSocial</span>
          <button class="esocial-modal__close" @click="close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="esocial-modal__body">
          <div class="rotina-list" v-loading="loading">
            <div v-if="!loading && rotinas.length == 0" class="rotina-empty">
              Nenhuma rotina configurada.
            </div>

            <div
              class="rotina-card"
              :class="'rotina-card--' + statusModifier(rotina)"
              v-for="rotina in rotinas"
              :key="rotina.signature"
            >
              <div class="rotina-card__header">
                <div class="rotina-card__title">
                  <i :class="rotinaIcon(rotina.signature)"></i>
                  <span>{{ rotina.label }}</span>
                  <el-tooltip effect="dark" placement="top" :content="rotinaDescricao(rotina.signature)">
                    <i class="fa fa-question-circle rotina-card__help"></i>
                  </el-tooltip>
                </div>

                <div class="rotina-card__status" :class="'rotina-card__status--' + statusModifier(rotina)">
                  <i :class="statusIcon(rotina)"></i> {{ statusLabel(rotina) }}
                </div>
              </div>

              <div class="rotina-card__meta">
                <div class="rotina-meta">
                  <span class="rotina-meta__label">Última execução</span>
                  <span class="rotina-meta__value">
                    {{ rotina.ultima_execucao ? toDate(rotina.ultima_execucao) : "Nunca executado" }}
                  </span>
                  <span class="rotina-meta__relative" v-if="rotina.ultima_execucao">{{ fromNow(rotina.ultima_execucao) }}</span>
                </div>

                <div class="rotina-meta">
                  <span class="rotina-meta__label">Próxima execução</span>
                  <span class="rotina-meta__value">{{ toDate(rotina.proxima_execucao) }}</span>
                  <span class="rotina-meta__relative">{{ fromNow(rotina.proxima_execucao) }}</span>
                </div>
              </div>

              <button class="rotina-card__toggle" @click="toggleHistorico(rotina.signature)">
                <i class="fa" :class="historico == rotina.signature ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                {{ historico == rotina.signature ? "Ocultar histórico" : "Ver histórico" }}
              </button>

              <transition name="esocial-modal-fade">
                <div class="rotina-historico" v-if="historico == rotina.signature">
                  <div v-if="loadingHistorico" class="rotina-historico__loading">
                    <i class="fa fa-refresh fa-spin"></i> Carregando histórico...
                  </div>

                  <div v-else-if="historicoItens.length == 0" class="rotina-historico__empty">
                    Nenhuma execução registrada ainda.
                  </div>

                  <template v-else>
                    <div
                      class="rotina-historico__item"
                      :class="{ 'rotina-historico__item--error': item.executado == 0 }"
                      v-for="item in historicoItens"
                      :key="item.id"
                    >
                      <i class="fa" :class="item.executado == 0 ? 'fa-times-circle' : 'fa-check-circle'"></i>
                      <div class="rotina-historico__content">
                        <span class="rotina-historico__msg">{{ item.msg || "-" }}</span>

                        <div class="rotina-historico__resumo" v-if="resumoDe(item).length">
                          <span class="rotina-historico__resumo-item" v-for="(r, idx) in resumoDe(item)" :key="idx">
                            {{ r.label }}: <b>{{ r.valor }}</b>
                          </span>
                        </div>

                        <div class="rotina-historico__erros" v-if="errosDe(item).length">
                          <span class="rotina-historico__erro" v-for="(erro, idx) in errosDe(item)" :key="idx">
                            {{ formatErro(erro) }}
                          </span>
                        </div>

                        <span class="rotina-historico__date">{{ toDate(item.updated_at) }}</span>
                      </div>
                    </div>
                  </template>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Fct from "common/fct";
import Rest from "common/rest";
import moment from "moment";

const ICONES_ROTINA = {
  "command:esocial.gerarLote": "fa fa-folder-open-o",
  "command:esocial.processarLote": "fa fa-paper-plane",
  "command:esocial.consultarLote": "fa fa-search",
  "command:esocial.gerarS2240Admissional": "fa fa-user-plus",
  "command:esocial.reenviarS2240Erros": "fa fa-repeat",
};

const DESCRICOES_ROTINA = {
  "command:esocial.gerarLote":
    "Agrupa os eventos do eSocial que ainda não foram enviados em lotes, por empresa e layout. Só organiza os eventos em lote, não envia nada ao governo.",
  "command:esocial.processarLote":
    "Envia ao webservice do eSocial os lotes que ainda não foram processados. É aqui que o envio de verdade acontece.",
  "command:esocial.consultarLote":
    "Consulta no eSocial o resultado do processamento dos lotes já enviados e atualiza o status de cada evento (confirmado ou com erro).",
  "command:esocial.gerarS2240Admissional":
    "Cria automaticamente o evento S-2240 (exposição a agentes de risco) 48h depois da confirmação do S-2220 admissional do trabalhador.",
  "command:esocial.reenviarS2240Erros":
    "Reenvia sozinho o lote de S-2240 quando detecta que o cadastro do funcionário foi corrigido depois de um erro do webservice (ex: categoria ou matrícula faltando).",
};

export default {
  name: "esocial-agendamento",
  mixins: [Rest, Fct],

  props: {
    showForm: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    showForm() {
      this.showModal = !this.showModal;

      if (this.showModal) {
        this.getAgendamento();
      } else {
        this.historico = null;
      }
    },
  },

  data() {
    return {
      showModal: false,
      loading: false,
      rotinas: [],
      historico: null,
      loadingHistorico: false,
      historicoItens: [],
    };
  },

  methods: {
    close() {
      this.showModal = false;
      this.historico = null;
    },

    getAgendamento() {
      this.loading = true;

      this.get("esocial/agendamento").then((result) => {
        result = result[0];
        this.rotinas = result.status == 200 ? result.data : [];
        this.loading = false;
      });
    },

    toggleHistorico(signature) {
      if (this.historico == signature) {
        this.historico = null;
        return;
      }

      this.historico = signature;
      this.loadingHistorico = true;
      this.historicoItens = [];

      this.get("log/tarefa", { filter: { tarefa: signature }, pagination: 10 }).then((result) => {
        result = result[0];
        this.historicoItens = result.status == 200 ? result.data.data : [];
        this.loadingHistorico = false;
      });
    },

    rotinaIcon(signature) {
      return ICONES_ROTINA[signature] || "fa fa-cog";
    },

    rotinaDescricao(signature) {
      return DESCRICOES_ROTINA[signature] || "Sem descrição cadastrada para esta rotina.";
    },

    resumoDe(item) {
      return item.conteudo && item.conteudo.resumo ? item.conteudo.resumo : [];
    },

    errosDe(item) {
      return item.conteudo && item.conteudo.erros ? item.conteudo.erros : [];
    },

    formatErro(erro) {
      if (typeof erro == "string") return erro;
      if (erro && erro.error) return `Lote ${erro.lote} (empresa ${erro.empresa}): ${erro.error}`;

      return JSON.stringify(erro);
    },

    fromNow(date) {
      return moment(date).fromNow();
    },

    statusModifier(rotina) {
      if (rotina.processando) return "processando";
      if (!rotina.ultima_execucao) return "inedita";
      if (rotina.executado == 0) return "erro";

      return "ok";
    },

    statusIcon(rotina) {
      const icones = {
        processando: "fa fa-refresh fa-spin",
        inedita: "fa fa-minus-circle",
        erro: "fa fa-times-circle",
        ok: "fa fa-check-circle",
      };

      return icones[this.statusModifier(rotina)];
    },

    statusLabel(rotina) {
      const labels = {
        processando: "Processando",
        inedita: "Nunca executado",
        erro: "Falhou",
        ok: "OK",
      };

      return labels[this.statusModifier(rotina)];
    },
  },
};
</script>

<style lang="scss" scoped>
.esocial-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
}

.esocial-modal {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  width: 620px;
  max-width: 92vw;
  max-height: 85vh;
  overflow: hidden;

  &--wide {
    width: 90%;
    max-width: 780px;
  }
}

.esocial-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 3px solid rgba(168, 121, 31, 0.45);
}

.esocial-modal__title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2329;
}

.esocial-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #f1f2f4;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: #e4e7ed;
    color: #1f2329;
  }
}

.esocial-modal__body {
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1 1 auto;
}

.esocial-modal-fade-enter-active,
.esocial-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.esocial-modal-fade-enter,
.esocial-modal-fade-leave-to {
  opacity: 0;
}

.rotina-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 60px;
}

.rotina-empty {
  text-align: center;
  color: #9aa0a8;
  padding: 20px 0;
}

.rotina-card {
  border: 1px solid #eef0f2;
  border-radius: 10px;
  padding: 14px 16px;

  &--ok {
    border-color: rgba(26, 138, 62, 0.4);
  }

  &--erro {
    border-color: rgba(192, 57, 43, 0.4);
  }

  &--processando {
    border-color: rgba(0, 153, 255, 0.4);
  }
}

.rotina-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.rotina-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2329;

  i {
    color: #9aa0a8;
    font-size: 12px;
  }
}

.rotina-card__help {
  cursor: help;
  transition: color 0.15s;

  &:hover {
    color: #09f !important;
  }
}

.rotina-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #9aa0a8;

  &--ok {
    color: #1a8a3e;
  }

  &--erro {
    color: #c0392b;
  }

  &--processando {
    color: #09f;
  }
}

.rotina-card__meta {
  display: flex;
  gap: 28px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.rotina-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rotina-meta__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9aa0a8;
}

.rotina-meta__value {
  font-size: 12px;
  color: #1f2329;
}

.rotina-meta__relative {
  font-size: 11px;
  color: #9aa0a8;
  font-style: italic;
}

.rotina-card__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #1f2329;
  }
}

.rotina-historico {
  margin-top: 10px;
  padding: 4px 12px;
  background: #fafbfc;
  border: 1px solid #eef0f2;
  border-radius: 8px;
}

.rotina-historico__loading,
.rotina-historico__empty {
  text-align: center;
  color: #9aa0a8;
  font-size: 12px;
  padding: 14px 0;
}

.rotina-historico__item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #eef0f2;
  font-size: 12px;
  color: #1a8a3e;

  &:last-child {
    border-bottom: none;
  }

  &--error {
    color: #c0392b;
  }

  i {
    margin-top: 1px;
    font-size: 11px;
  }
}

.rotina-historico__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rotina-historico__msg {
  color: #1f2329;
}

.rotina-historico__resumo {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 2px 0;
}

.rotina-historico__resumo-item {
  font-size: 11px;
  color: #6b7280;

  b {
    color: #1f2329;
  }
}

.rotina-historico__erros {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 0;
}

.rotina-historico__erro {
  font-size: 11px;
  color: #c0392b;
  background: rgba(192, 57, 43, 0.08);
  border-radius: 6px;
  padding: 6px 8px;
}

.rotina-historico__date {
  font-size: 11px;
  color: #9aa0a8;
}
</style>



// WEBPACK FOOTER //
// agendamento.vue?1ad0a5b6